/**
 * Phone number utility functions — international E.164 aware.
 */

/**
 * Format an E.164 phone number for display.
 * US numbers (+1…) are shown as (xxx) xxx-xxxx.
 * Other numbers are returned as-is (E.164 format).
 * @param {string} phoneNumber - E.164 phone string
 * @returns {string}
 */
export const formatPhoneNumberDisplay = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;
  const str = String(phoneNumber).trim();

  if (str.startsWith('+1') && str.length === 12) {
    const digits = str.slice(2);
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (str.startsWith('+')) {
    return str;
  }

  const cleaned = str.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phoneNumber;
};

/**
 * Extract the numeric digits (and leading +) from a phone number for tel: links.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const extractPhoneDigits = (phoneNumber) => {
  if (!phoneNumber) return '';
  return String(phoneNumber).replace(/[^\d+]/g, '');
};

/**
 * Check if a string looks like a phone number.
 * @param {string} text
 * @returns {boolean}
 */
export const isPhoneNumber = (text) => {
  if (!text) return false;
  const str = String(text).trim();
  if (str.startsWith('+') && str.length >= 8) return true;
  const cleaned = str.replace(/\D/g, '');
  return cleaned.length >= 10;
};

/**
 * Build E.164 from a country code prefix and a national number.
 * @param {string} countryCode - e.g. "+1", "+44"
 * @param {string} nationalNumber - digits only
 * @returns {string}
 */
export const buildE164 = (countryCode, nationalNumber) => {
  const code = (countryCode || '+1').replace(/[^\d+]/g, '');
  const number = (nationalNumber || '').replace(/\D/g, '');
  return `${code}${number}`;
};

/**
 * Sanitize raw phone input to E.164-safe format.
 * Strips everything except digits and +, ensures + prefix.
 * Use on full E.164 phone fields (not national-only inputs).
 * @param {string} value - Raw input from user
 * @returns {string}
 */
export const sanitizePhoneInput = (value) => {
  if (!value) return '';
  const cleaned = String(value).replace(/[^\d+]/g, '');
  if (!cleaned) return '+';
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

/**
 * Format a contact info string that might contain a phone number.
 * @param {string} contactInfo
 * @returns {string}
 */
export const formatContactInfo = (contactInfo) => {
  if (!contactInfo) return contactInfo;
  const trimmed = String(contactInfo).trim();
  if (isPhoneNumber(trimmed)) {
    return formatPhoneNumberDisplay(trimmed);
  }
  return contactInfo;
};
